import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, token } = useAuth();

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setWishlist([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wishlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWishlist(data.product_ids || []);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const addToWishlist = useCallback(async (productId) => {
    if (!isAuthenticated || !token) {
      return { success: false, message: 'Please sign in to add items to your wishlist' };
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId })
      });

      if (response.ok) {
        setWishlist(prev => [...new Set([...prev, productId])]);
        return { success: true, message: 'Added to wishlist!' };
      }
      
      const error = await response.json();
      return { success: false, message: error.detail || 'Failed to add to wishlist' };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { success: false, message: 'Failed to add to wishlist' };
    }
  }, [isAuthenticated, token]);

  const removeFromWishlist = useCallback(async (productId) => {
    if (!isAuthenticated || !token) {
      return { success: false, message: 'Please sign in' };
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId })
      });

      if (response.ok) {
        setWishlist(prev => prev.filter(id => id !== productId));
        return { success: true, message: 'Removed from wishlist' };
      }
      
      const error = await response.json();
      return { success: false, message: error.detail || 'Failed to remove from wishlist' };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { success: false, message: 'Failed to remove from wishlist' };
    }
  }, [isAuthenticated, token]);

  const isInWishlist = useCallback((productId) => {
    return wishlist.includes(productId);
  }, [wishlist]);

  const toggleWishlist = useCallback(async (productId) => {
    if (isInWishlist(productId)) {
      return removeFromWishlist(productId);
    }
    return addToWishlist(productId);
  }, [isInWishlist, addToWishlist, removeFromWishlist]);

  const clearWishlist = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    try {
      await fetch(`${API_URL}/api/wishlist/clear`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWishlist([]);
    } catch (error) {
      console.error('Error clearing wishlist:', error);
    }
  }, [isAuthenticated, token]);

  return (
    <WishlistContext.Provider value={{
      wishlist,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      clearWishlist,
      wishlistCount: wishlist.length
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export default WishlistContext;
